import { useCallback, useContext, useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { MergeListingsModal } from 'src/modals/MergeListings';
import { BulkMergeListingsModal } from 'src/modals/MergeListings/BulkMergeListings';
import { MergeListingsIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Permission } from 'src/WebApiController';

import * as styles from './EventActions.css';

export const LaunchMergeListings = ({
  event,
  entityCount,
}: {
  event?: Event | undefined;
  entityCount?: number;
}) => {
  const { isDarkMode } = useSiteTheme();
  const elementRef = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(elementRef);
  const { setModal } = useContext(ModalContext);
  const {
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  const hasUpdateListingInfoPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_UpdateListingInfo
  );

  const mergeListings = useCallback(() => {
    setModal({
      children:
        event != null ? (
          <MergeListingsModal event={event} />
        ) : (
          <BulkMergeListingsModal />
        ),
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  }, [setModal, event]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }

      mergeListings();
    },
    [event, mergeListings, setListItemExpansion]
  );

  const iconColor = useMemo(() => {
    if (isHovering) {
      return vars.color.iconHover;
    }
    return vars.color.textPrimary;
  }, [isHovering]);

  if (!entityCount || entityCount < 2 || !hasUpdateListingInfoPermission) {
    return null;
  }

  return (
    <Tooltip
      open={isHovering}
      delayDuration={100}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
      triggerContent={
        <Stack
          ref={elementRef}
          onClick={onLaunchDialog}
          className={styles.eventActionsGroupItem}
        >
          <MergeListingsIcon
            withHoverEffect
            fill={iconColor}
            size={vars.iconSize.l}
          />
        </Stack>
      }
    >
      <Content id={ContentId.MergeListings} />
    </Tooltip>
  );
};
